/* =============================================================================
   beUNIQ — product comparison picker

   Search runs against Shopify's own /search/suggest.json. The chosen product's
   column comes from the Section Rendering API, so its metafields, prices and
   locale are rendered by Liquid rather than rebuilt here.

   Dependency-free and registered with a plain script tag, so it needs no entry
   in the theme's import map.
   ========================================================================== */

(() => {
  'use strict'

  const SECTION_ID = 'beuniq-compare-card'
  const DEBOUNCE_MS = 250
  const MIN_CHARS = 2

  const debounce = (fn, wait) => {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn(...args), wait)
    }
  }

  class BeuniqCompare extends HTMLElement {
    connectedCallback() {
      // Named `pane`, never `slot`: Element.slot is a reserved reflected
      // property, so assigning a node to this.slot silently coerces it to a
      // string and every later DOM call on it throws.
      this.pane = this.querySelector('[data-compare-slot]')
      if (!this.pane) return

      this.limit = parseInt(this.dataset.limit, 10) || 6
      this.currentHandle = this.dataset.currentHandle || ''
      this.pickerHTML = this.pane.innerHTML
      this.items = []
      this.activeIndex = -1
      this.controller = null

      this.onInput = debounce(() => this.search(), DEBOUNCE_MS)
      this.onKeydown = this.handleKeydown.bind(this)
      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.close()
      }

      this.bindPicker()
      this.addEventListener('click', this.handleClick.bind(this))
      document.addEventListener('click', this.onDocClick)
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick)
      if (this.controller) this.controller.abort()
    }

    /** Re-resolve picker nodes and listeners; called on first load and after reset. */
    bindPicker() {
      this.input = this.querySelector('[data-compare-input]')
      this.results = this.querySelector('[data-compare-results]')
      this.status = this.querySelector('[data-compare-status]')

      if (!this.input) return
      this.input.addEventListener('input', this.onInput)
      this.input.addEventListener('keydown', this.onKeydown)
    }

    handleClick(e) {
      if (e.target.closest('[data-compare-clear]')) {
        e.preventDefault()
        this.reset()
        return
      }
      const option = e.target.closest('[data-handle-option]')
      if (option) {
        e.preventDefault()
        this.select(option.getAttribute('data-handle-option'))
      }
    }

    handleKeydown(e) {
      if (e.key === 'Escape') return this.close()
      if (!this.items.length) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const dir = e.key === 'ArrowDown' ? 1 : -1
        this.activeIndex = (this.activeIndex + dir + this.items.length) % this.items.length
        this.highlight()
      } else if (e.key === 'Enter' && this.activeIndex > -1) {
        e.preventDefault()
        this.select(this.items[this.activeIndex].handle)
      }
    }

    highlight() {
      this.results.querySelectorAll('[data-handle-option]').forEach((node, i) => {
        const on = i === this.activeIndex
        node.classList.toggle('is-active', on)
        node.setAttribute('aria-selected', on ? 'true' : 'false')
      })
    }

    close() {
      if (!this.results) return
      this.results.hidden = true
      this.input?.setAttribute('aria-expanded', 'false')
      this.activeIndex = -1
    }

    say(msg) {
      if (this.status) this.status.textContent = msg || ''
    }

    async search() {
      const q = this.input.value.trim()
      if (q.length < MIN_CHARS) {
        this.close()
        return this.say('')
      }

      // Drop any in-flight request so a slower earlier one cannot overwrite
      // newer results.
      if (this.controller) this.controller.abort()
      this.controller = new AbortController()

      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=${this.limit}`
      this.say('Searching…')

      try {
        const res = await fetch(url, { signal: this.controller.signal })
        if (!res.ok) throw new Error(`Search failed: ${res.status}`)
        const data = await res.json()
        const products = data?.resources?.results?.products ?? []
        // Never offer the product already being viewed.
        this.items = products.filter((p) => p.handle !== this.currentHandle)
        this.render()
      } catch (err) {
        if (err.name === 'AbortError') return
        this.close()
        this.say('Could not search right now. Please try again.')
      }
    }

    render() {
      if (!this.items.length) {
        this.close()
        return this.say('No other rugs matched that search.')
      }

      this.results.replaceChildren(
        ...this.items.map((p) => {
          const li = document.createElement('li')
          li.className = 'bq-cmp__result'
          li.setAttribute('role', 'option')
          li.setAttribute('aria-selected', 'false')
          li.setAttribute('data-handle-option', p.handle)

          const src = p.featured_image?.url || p.image
          if (src) {
            const img = document.createElement('img')
            img.src = src
            img.alt = ''
            img.width = 40
            img.height = 40
            img.loading = 'lazy'
            li.appendChild(img)
          }

          const span = document.createElement('span')
          span.className = 'bq-cmp__result-title'
          // textContent, never innerHTML — product titles are untrusted input.
          span.textContent = p.title
          li.appendChild(span)

          return li
        })
      )

      this.results.hidden = false
      this.input.setAttribute('aria-expanded', 'true')
      this.activeIndex = -1
      this.say('')
    }

    async select(handle) {
      if (!handle) return

      this.close()
      this.say('Loading comparison…')
      this.pane.setAttribute('aria-busy', 'true')

      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`)
        if (!res.ok) throw new Error(`Card failed: ${res.status}`)
        const html = await res.text()

        // The Section Rendering API wraps output in a section element; lift the
        // card out so it lands as a direct grid child and aligns with the rest.
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const card = doc.querySelector('[data-compare-card]')
        if (!card) throw new Error('No card in response')

        this.pane.replaceChildren(card)
        this.pane.classList.add('is-filled')
        this.say('')
      } catch {
        this.say('Could not load that rug. Please pick another.')
      } finally {
        this.pane.removeAttribute('aria-busy')
      }
    }

    reset() {
      this.pane.classList.remove('is-filled')
      this.pane.innerHTML = this.pickerHTML
      this.items = []
      this.activeIndex = -1
      this.bindPicker()
      this.input?.focus()
    }
  }

  if (!customElements.get('beuniq-compare')) {
    customElements.define('beuniq-compare', BeuniqCompare)
  }
})()
